import { checkSession } from "@features/auth";
import { getUserOrganizationId } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { Input } from "@shadcn/ui/input";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { z } from "zod";
import { ServiceBreadcrumb } from "~/components/lock/ServiceBreadcrumb";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { DeliveryRequestList } from "~/components/warenannahme/DeliveryRequestList";
import { WarenannahmeCard } from "~/components/warenannahme/WarenannahmeCard";
import { warenannahmeItems } from "~/components/warenannahme/warenannahmeItems";
import type { warenannahmeOverviewQuery } from "./__generated__/warenannahmeOverviewQuery.graphql";

export const query = graphql`
	query warenannahmeOverviewQuery($hubName: String!, $skipOwnedReservations: Boolean!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			organization {
				id
			}
			hub @required(action: THROW) {
				id
			}
		}
		deliveryRequests(filter: { direction: RECEIVED, status: [OPEN] }, pagination: { page: 0, pageSize: 100 }) {
			nodes {
				id
				hubId
				createdAt
				content
				preferredSize
				requesterOrganization {
					id
					name
				}
			}
		}
		myUtilizationGrants(input: {}, pagination: { page: 0, pageSize: 1000 }) {
			nodes {
				id
				deactivatedAt
				permissions
				reservation {
					id
					startedAt
					deliveredAt
					deactivatedAt
					contentNote
					note
					flow
					unlockRequests(pagination: { page: 0, pageSize: 1 }) @catch(to: NULL) {
						totalCount
					}
					subtype {
						__typename
						... on B2BlockReservationSubtype {
							organization {
								id
							}
						}
					}
					space {
						frame
						position
						configuration
						hub {
							id
						}
					}
					...WarenannahmeCardFragment
				}
			}
		}
		lockReservations(
			filter: { subtype: B2B, flow: GOODS_RECEPTION, active: true }
			orderBy: [{ field: STARTED_AT, ordering: DESC }]
			pagination: { page: 0, pageSize: 1000 }
		) @skip(if: $skipOwnedReservations) {
			nodes {
				id
				startedAt
				deliveredAt
				deactivatedAt
				contentNote
				note
				flow
				unlockRequests(pagination: { page: 0, pageSize: 1 }) @catch(to: NULL) {
					totalCount
				}
				subtype {
					__typename
					... on B2BlockReservationSubtype {
						organization {
							id
						}
					}
				}
				space {
					frame
					position
					configuration
					hub {
						id
					}
				}
				...WarenannahmeCardFragment
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/warenannahme/")({
	beforeLoad: () => {
		document.title = "bacehub | Warenannahme";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ done: z.enum(["delivered", "collected", "removed"]).optional() })),
	loader: async (ctx) => {
		if (!(await checkSession())) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/warenannahme` },
			});
		}
		return preloadQuery<warenannahmeOverviewQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
			skipOwnedReservations: !getUserOrganizationId(),
		});
	},
});

function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { done } = Route.useSearch();
	const data = usePreloadedQuery<warenannahmeOverviewQuery>(query, Route.useLoaderData());
	const hubMetaName = data.hubMeta.name;
	const [search, setSearch] = useState("");

	useEffect(() => {
		if (!done) return;
		toast.success(
			done === "delivered" ? i18n.WA_DELIVERED_TOAST() : done === "removed" ? i18n.WA_REMOVED_TOAST() : i18n.WA_COLLECTED_TOAST()
		);
		navigate({ to: "/$hubMetaName/warenannahme", params: { hubMetaName }, search: {}, replace: true });
	}, [done, hubMetaName, i18n, navigate]);

	const items = warenannahmeItems({
		hubId: data.hubMeta.hub.id,
		owned: data.lockReservations?.nodes ?? [],
		grants: data.myUtilizationGrants.nodes,
		includeEnded: false,
		organizationId: getUserOrganizationId(),
		hubOwnerId: data.hubMeta.organization.id,
	});
	const needle = search.trim().toLowerCase();
	const matching = needle
		? items.filter((item) =>
				[item.compartmentNumber, item.content, item.comment].some((text) => (text ?? "").toLowerCase().includes(needle))
			)
		: items;
	// Only what is still waiting in the hub is shown: who collected what stays in the dashboard.
	const organizationId = getUserOrganizationId();
	const requests = organizationId
		? data.deliveryRequests.nodes.filter(
				(request) => request.hubId === data.hubMeta.hub.id && request.requesterOrganization.id !== organizationId
			)
		: [];
	const deliverRequest = (request: (typeof requests)[number] | undefined) => {
		if (!request) return;
		navigate({
			to: "/$hubMetaName/warenannahme/deliver",
			params: { hubMetaName },
			search: { requestId: request.id, start: true },
		});
	};
	// The hub owner only collects: it skips the choice between delivering and collecting.
	const isHubOwner = organizationId === data.hubMeta.organization.id;
	const startAction = () =>
		navigate({ to: isHubOwner ? "/$hubMetaName/warenannahme/pickup" : "/$hubMetaName/warenannahme/action", params: { hubMetaName } });

	return (
		<PageContainer
			footer={
				items.length > 0 || requests.length > 0 ? (
					<Button className="w-full shadow-xl" size="lg" onClick={startAction}>
						<Plus className="size-4" />
						<T>{isHubOwner ? i18n.WA_COLLECT_CTA() : i18n.WA_NEW_ACTION()}</T>
					</Button>
				) : null
			}
			footerStyles="fixed inset-x-0 bottom-0 z-10 border-t bg-background px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:static md:border-t-0 md:bg-transparent md:p-0 md:px-6"
		>
			<AppHeader service="LOCK_B2B" />
			<div className="flex flex-col gap-6 px-6 pt-6 pb-6">
				<div className="flex flex-col gap-2">
					<ServiceBreadcrumb hubMetaName={hubMetaName} current={i18n.WA_TITLE()} />
					<h1 className="text-2xl font-breit font-medium">
						<T>{i18n.WA_OVERVIEW_TITLE()}</T>
					</h1>
				</div>

				{/* "Ask to deliver" is hidden for now.
				{requests.length > 0 ? <DeliveryRequestList requests={requests} onSelect={deliverRequest} /> : null}
				*/}

				<div className="relative">
					<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder={i18n.WA_SEARCH_PLACEHOLDER()}
						aria-label={i18n.WA_SEARCH_PLACEHOLDER()}
						className="h-9 rounded-lg bg-background pl-9 pr-3 text-sm shadow-sm"
					/>
				</div>

				<section className="flex flex-col gap-4" aria-labelledby="waiting-title">
					<h2 id="waiting-title" className="text-sm font-breit font-medium">
						<T>{i18n.WA_TAB_DELIVERED()}</T> ({matching.length})
					</h2>
					{matching.length > 0 ? (
						matching.map((item) => (
							<WarenannahmeCard
								key={item.key}
								reservation={item.reservation}
								removesDelivery={!isHubOwner}
								showDeliverer={isHubOwner}
								onCollect={
									item.canOpen
										? () =>
												navigate({
													to: "/$hubMetaName/warenannahme/pickup",
													params: { hubMetaName },
													search: { reservationId: item.reservationId },
												})
										: undefined
								}
							/>
						))
					) : (
						<EmptyState
							searching={needle.length > 0}
							onStart={items.length === 0 && requests.length === 0 && !isHubOwner ? startAction : undefined}
						/>
					)}
				</section>
			</div>
		</PageContainer>
	);
}

function EmptyState(props: { searching: boolean; onStart?: () => void }) {
	const i18n = useI18nContext().LL["new-ui"];
	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col items-center gap-2 rounded-[14px] border bg-background px-6 py-6 text-center shadow-xs">
				<p className="text-sm font-breit font-medium">
					<T>{props.searching ? i18n.WA_EMPTY_SEARCH() : i18n.WA_EMPTY_TITLE()}</T>
				</p>
				{props.searching ? null : (
					<p className="text-sm text-muted-foreground">
						<T>{i18n.WA_EMPTY_DESC()}</T>
					</p>
				)}
			</div>
			{props.onStart ? (
				<Button className="w-full shadow-xl" onClick={props.onStart}>
					<Plus className="size-4" />
					<T>{i18n.WA_NEW_ACTION()}</T>
				</Button>
			) : null}
		</div>
	);
}
